import { motion } from "framer-motion";
import { Target, Lightbulb, Users, Heart, Shield, Zap } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Section } from "@/components/layout/Section";
import { Container } from "@/components/layout/Container";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AnimatedSection } from "@/components/ui/animated-section";
import { staggerContainer, staggerItem } from "@/lib/animations";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

// Team members placeholder
const team = [
  { name: "Anthony Sinclair", role: "CEO & Founder", initials: "AS" },
  { name: "Laura Hall", role: "Senior PM", initials: "LH" },
  { name: "Jamie White", role: "Head of AI", initials: "JW" },
  { name: "Helena Sinclair", role: "Head of Design", initials: "HS" },
];

// Company values
const values = [
  {
    icon: Target,
    title: "User-Focused",
    description: "Every feature we build starts with understanding our users' real challenges and needs.",
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "We push the boundaries of AI to make survey creation faster and more intuitive.",
  },
  {
    icon: Shield,
    title: "Trust & Security",
    description: "Your data security is paramount. We maintain the highest standards of protection.",
  },
  {
    icon: Heart,
    title: "Simplicity",
    description: "Complex technology should feel simple. We obsess over making things easy to use.",
  },
];

export default function About() {
  useDocumentMeta({
    title: 'About Us',
    description: "Learn about SurviAI's mission to transform paper surveys into digital experiences using AI."
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        {/* Hero Section - Mission Statement */}
        <Section className="pt-24 md:pt-32 pb-16">
          <Container>
            <AnimatedSection className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Making Survey Creation <span className="text-gradient">Effortless</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                We believe researchers, educators, and teams shouldn't spend hours manually creating surveys. 
                Our mission is to transform any document into a professional digital survey in minutes, 
                powered by AI that actually understands your content.
              </p>
            </AnimatedSection>
          </Container>
        </Section>

        {/* The Problem We Solve */}
        <Section background="muted">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <AnimatedSection>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                  The Problem We Solve
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                  Every year, millions of hours are wasted manually transcribing paper surveys, 
                  PDFs, and documents into digital formats. Research teams, HR departments, and 
                  educators spend countless hours on tedious data entry instead of focusing on 
                  what matters: gathering insights and making decisions.
                  </p>
                  <p>
                    Traditional survey tools require you to start from scratch, even when you 
                    already have perfectly good questions written in existing documents. This 
                    creates unnecessary friction and delays in the research process.
                  </p>
                  <p className="font-medium text-foreground">
                    We built SurviAI to eliminate this friction entirely.
                  </p>
                </div>
              </AnimatedSection>
              
              <AnimatedSection delay={0.2}>
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-6 text-center hover-lift">
                    <div className="text-4xl font-bold text-primary mb-2">80%</div>
                    <div className="text-sm text-muted-foreground">Time Saved</div>
                  </Card>
                  <Card className="p-6 text-center hover-lift">
                    <div className="text-4xl font-bold text-primary mb-2">98%</div>
                    <div className="text-sm text-muted-foreground">AI Accuracy</div>
                  </Card>
                  <Card className="p-6 text-center hover-lift">
                    <div className="text-4xl font-bold text-primary mb-2">10K+</div>
                    <div className="text-sm text-muted-foreground">Surveys Created</div>
                  </Card>
                  <Card className="p-6 text-center hover-lift">
                    <div className="text-4xl font-bold text-primary mb-2">500+</div>
                    <div className="text-sm text-muted-foreground">Teams Trust Us</div>
                  </Card>
                </div>
              </AnimatedSection>
            </div>
          </Container>
        </Section>

        {/* Our Story */}
        <Section>
          <Container>
            <AnimatedSection className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
                <p>
                  SurviAI was born out of frustration. Our founders, while working in research 
                  and academia, spent far too many late nights manually converting paper surveys 
                  and legacy documents into digital formats.
                </p>
                <p>
                  In 2024, they asked a simple question: "Why can't AI just read our documents 
                  and create the survey for us?" That question became our mission.
                </p>
                <p>
                  Today, SurviAI uses advanced AI to understand document structure, extract 
                  questions intelligently, and create professional surveys that would have 
                  taken hours to build manually, all in minutes.
                </p>
              </div>
            </AnimatedSection>
          </Container>
        </Section>

        {/* Team Section */}
        <Section background="muted">
          <Container>
            <AnimatedSection className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Meet the Team
              </h2>
              <p className="text-lg text-muted-foreground">
                A passionate team dedicated to transforming how surveys are created
              </p>
            </AnimatedSection>
            
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {team.map((member) => (
                <motion.div key={member.name} variants={staggerItem}>
                  <Card className="text-center p-6 hover-lift">
                    <CardContent className="p-0">
                      <Avatar className="h-20 w-20 mx-auto mb-4">
                        <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                          {member.initials}
                        </AvatarFallback>
                      </Avatar>
                      <h3 className="font-semibold text-foreground">{member.name}</h3>
                      <p className="text-sm text-muted-foreground">{member.role}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </Container>
        </Section>

        {/* Values Section */}
        <Section>
          <Container>
            <AnimatedSection className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Our Values
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                The principles that guide everything we do
              </p>
            </AnimatedSection>
            
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {values.map((value) => (
                <motion.div key={value.title} variants={staggerItem}>
                  <Card className="h-full hover-lift">
                    <CardContent className="pt-6 text-center">
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <value.icon className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="font-semibold text-foreground mb-2">{value.title}</h3>
                      <p className="text-sm text-muted-foreground">{value.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </Container>
        </Section>

        {/* Join Us CTA */}
        <Section background="muted">
          <Container>
            <AnimatedSection className="text-center max-w-2xl mx-auto">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Join Our Team
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We're always looking for talented individuals who are passionate about 
                AI, research, and building products that make a difference.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg">
                    <Zap className="mr-2 h-4 w-4" />
                    View Open Positions
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </Container>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
